/**
 * Formatação de telefone brasileiro.
 *
 * Padrões suportados:
 *   - Celular: (11) 91234-5678  (11 dígitos)
 *   - Fixo:    (11) 1234-5678   (10 dígitos)
 *
 * Não suporta DDI internacional ainda (futuro: +55, +1, etc.).
 */

function onlyDigits(s: string): string {
  return s.replace(/\D/g, '');
}

/**
 * Formata telefone com máscara completa.
 *
 * formatPhone('11912345678')    → "(11) 91234-5678"
 * formatPhone('1132145678')     → "(11) 3214-5678"
 * formatPhone('912345678')      → "—" (incompleto)
 */
export function formatPhone(input: string | null | undefined): string {
  if (!input) return '—';
  const d = onlyDigits(input);
  if (d.length === 11) {
    // celular: (XX) XXXXX-XXXX
    return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7, 11)}`;
  }
  if (d.length === 10) {
    // fixo: (XX) XXXX-XXXX
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6, 10)}`;
  }
  return '—';
}

/**
 * Máscara progressiva enquanto usuário digita (uso em onChange de input).
 *
 * maskPhone('11')           → "(11"
 * maskPhone('1191')         → "(11) 91"
 * maskPhone('11912345678')  → "(11) 91234-5678"
 */
export function maskPhone(input: string): string {
  const d = onlyDigits(input).slice(0, 11);
  if (d.length === 0) return '';
  if (d.length <= 2) return `(${d}`;
  if (d.length <= 6) return `(${d.slice(0, 2)}) ${d.slice(2)}`;
  if (d.length <= 10) {
    // Fixo
    return `(${d.slice(0, 2)}) ${d.slice(2, 6)}-${d.slice(6)}`;
  }
  // Celular (11 dígitos)
  return `(${d.slice(0, 2)}) ${d.slice(2, 7)}-${d.slice(7)}`;
}

/**
 * Valida formato (10 ou 11 dígitos numéricos).
 */
export function isValidPhone(input: string | null | undefined): boolean {
  if (!input) return false;
  const d = onlyDigits(input);
  return d.length === 10 || d.length === 11;
}

/**
 * Gera link clicável `tel:` removendo formatação.
 *
 * phoneToTelLink('(11) 91234-5678') → "tel:+5511912345678"
 */
export function phoneToTelLink(input: string | null | undefined): string | null {
  if (!input) return null;
  const d = onlyDigits(input);
  if (d.length !== 10 && d.length !== 11) return null;
  return `tel:+55${d}`;
}

/**
 * Gera link de WhatsApp.
 *
 * phoneToWhatsAppLink('(11) 91234-5678') → "https://wa.me/5511912345678"
 * phoneToWhatsAppLink('11912345678', 'Olá!') → "https://wa.me/5511912345678?text=Ol%C3%A1!"
 */
export function phoneToWhatsAppLink(
  input: string | null | undefined,
  message?: string
): string | null {
  if (!input) return null;
  const d = onlyDigits(input);
  if (d.length !== 10 && d.length !== 11) return null;
  const base = `https://wa.me/55${d}`;
  return message ? `${base}?text=${encodeURIComponent(message)}` : base;
}
